import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shell, PageHeader } from '../components/Shell.jsx';
import { Field, Input, Textarea } from '../components/Field.jsx';

export default function AddProspect() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState({});
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: '',
    outlet: '',
    outlet_url: '',
    beat: '',
    linkedin_url: '',
    twitter_handle: '',
    evidence: '',
    notes: '',
  });

  function update(k) {
    return e => {
      setForm(f => ({ ...f, [k]: e.target.value }));
      setErr(({ [k]: _gone, ...rest }) => rest);
    };
  }

  function normalizeUrl(s) {
    if (!s) return '';
    const trimmed = s.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return 'https://' + trimmed.replace(/^\/+/, '');
  }

  async function onSubmit(e) {
    e.preventDefault();
    const errors = {};
    if (!form.name.trim()) errors.name = 'name is required';
    if (!form.evidence.trim()) errors.evidence = 'evidence is required: cite the specific reason this person is a prospect';
    if (Object.keys(errors).length) { setErr(errors); return; }

    const payload = {
      ...form,
      outlet_url:   normalizeUrl(form.outlet_url),
      linkedin_url: normalizeUrl(form.linkedin_url),
      twitter_handle: form.twitter_handle.trim().replace(/^@/, ''),
    };

    setBusy(true);
    try {
      const r = await fetch('/api/prospects', {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr({ form: d.message || 'failed to create prospect' });
        return;
      }
      const d = await r.json();
      nav(`/prospects`, { replace: true });
    } catch (e) {
      setErr({ form: String(e.message || e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell narrow>
      <PageHeader
        eyebrow="Phase 2 manual entry"
        title="Add a prospect"
        sub="Add a named human who fits the press segment. Verified email + specific evidence required. No bulk lists."
      />

      <form onSubmit={onSubmit} className="stack-6">
        <div className="card card-pad">
          <h3 style={{ marginBottom: 'var(--space-4)' }}>Identity</h3>
          <div className="grid-2">
            <Field label="Name" required htmlFor="name" error={err.name}>
              <Input id="name" value={form.name} onChange={update('name')} placeholder="Jane Doe" autoFocus />
            </Field>
            <Field label="Role / Title" htmlFor="role" help="What they do at the outlet.">
              <Input id="role" value={form.role} onChange={update('role')} placeholder="Senior Editor" />
            </Field>
          </div>
          <div className="grid-2" style={{ marginTop: 'var(--space-4)' }}>
            <Field label="Email" htmlFor="email" help="Optional now, required before sending. Verification happens automatically.">
              <Input id="email" type="email" value={form.email} onChange={update('email')} placeholder="jane@constructiondive.com" />
            </Field>
            <Field label="Outlet" htmlFor="outlet">
              <Input id="outlet" value={form.outlet} onChange={update('outlet')} placeholder="Construction Dive" />
            </Field>
          </div>
        </div>

        <div className="card card-pad">
          <h3 style={{ marginBottom: 'var(--space-4)' }}>Beat &amp; coverage</h3>
          <div className="grid-2">
            <Field label="Beat" htmlFor="beat" help="What they cover. Used for fit scoring.">
              <Input id="beat" value={form.beat} onChange={update('beat')} placeholder="Building systems, roofing, construction tech" />
            </Field>
            <Field label="Outlet URL" htmlFor="outlet_url" help="www.re-dry.com is fine — I'll add https:// for you.">
              <Input id="outlet_url" value={form.outlet_url} onChange={update('outlet_url')} placeholder="constructiondive.com" />
            </Field>
          </div>
          <div className="grid-2" style={{ marginTop: 'var(--space-4)' }}>
            <Field label="LinkedIn URL" htmlFor="linkedin_url">
              <Input id="linkedin_url" value={form.linkedin_url} onChange={update('linkedin_url')} placeholder="linkedin.com/in/janedoe" />
            </Field>
            <Field label="Twitter/X handle" htmlFor="twitter_handle">
              <Input id="twitter_handle" value={form.twitter_handle} onChange={update('twitter_handle')} placeholder="janedoe" />
            </Field>
          </div>
        </div>

        <div className="card card-pad">
          <div className="row row-between" style={{ marginBottom: 'var(--space-3)' }}>
            <h3>Why is this person a prospect?</h3>
            <span className="badge badge-accent">Required</span>
          </div>
          <Field
            label="Evidence"
            required
            htmlFor="evidence"
            error={err.evidence}
            help="A byline link, a podcast episode, a property they own, a project they ran. The specific thing connecting them to your story. This becomes the opening reference in the pitch."
          >
            <Textarea
              id="evidence"
              value={form.evidence}
              onChange={update('evidence')}
              placeholder="e.g. Wrote March 14, 2026 piece in Construction Dive on PM-adhered vs mechanically fastened systems. Quoted Mark Graham at NRCA on moisture trapping in flat roofs."
            />
          </Field>
        </div>

        <div className="card card-pad">
          <h3 style={{ marginBottom: 'var(--space-3)' }}>Notes (optional)</h3>
          <Field label="Internal notes" htmlFor="notes" help="Anything Adam wants to remember. Not visible to the prospect.">
            <Textarea id="notes" value={form.notes} onChange={update('notes')} placeholder="Met at NRCA show 2024. Likes data-heavy stories." />
          </Field>
        </div>

        {err.form ? (
          <div className="card card-pad" style={{ borderColor: 'var(--danger)' }}>
            <div style={{ color: 'var(--danger)' }}>{err.form}</div>
          </div>
        ) : null}

        <div className="row" style={{ gap: 12 }}>
          <button type="submit" className="btn btn-lg" disabled={busy}>
            {busy ? <><span className="spinner" /> Adding</> : 'Add prospect'}
          </button>
          <Link to="/prospects" className="btn btn-ghost">Cancel</Link>
        </div>
      </form>
    </Shell>
  );
}
